import { memo, useMemo } from 'react';
import { FormProvider, useController } from 'react-hook-form';
import Icon from 'root/src/client/web/base/Icon';
import { colorsNew } from 'root/src/client/web/commonStyles';
import { deliveryRatioColor } from 'root/src/shared/constants/project';
import { platformIconMap } from 'root/src/shared/constants/dropKeys';
import { Platform } from 'root/src/shared/@types/platforms';
import { TWITCH_MIN_STREAM_TIME_MINUTES } from 'root/src/shared/deliveryRatio/deliveryRatio.constants';
import { isInitialBundle, isInitialCode, isInitialKey } from 'root/src/shared/rewards/helpers/initialProduct.guards';
import { KeyProductMnemonicIdResolver } from 'root/src/shared/rewards/services/productResolver.service';
import { useAccountData } from 'root/src/client/v2/userData/hooks/useAccountData';
import { useDeliveryRatio } from 'root/src/client/v2/userData/hooks/useDeliveryRatio';
import { KeySelectionModalContext, useDismissAlreadyOwnProductModal, useKeySelectionFormContext, useKeySelectionModalContext, } from 'root/src/client/v2/drop/features/KeySelectionModal/hooks/useKeySelectionModal';
import { useRedesignKeySelectionModal } from 'root/src/client/v2/drop/features/KeySelectionModal/hooks/useRedesignKeySelectionModal';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { Checkbox } from 'root/src/client/v2/common/components/ui/checkbox';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { RedesignModalShell } from 'root/src/client/v2/common/components/RedesignModalShell';
import { cn } from 'root/src/client/v2/common/helpers/cn';
const tileClass = (selected) => cn('flex min-w-[88px] flex-col items-center justify-center gap-2 rounded-xl border border-solid px-4 py-3 transition-colors', selected ? 'border-neon-cyan bg-neon-cyan/10 text-foreground' : 'border-white/[0.08] bg-white/[0.03] text-muted-foreground hover:border-white/20 hover:text-foreground');
const getDeliveryRatioColor = (ratio) => Object.entries(deliveryRatioColor)
    .reduce((acc, [threshold, text]) => (ratio >= Number(threshold) ? text : acc), deliveryRatioColor['0']);
const PlatformField = memo(() => {
    const form = useKeySelectionFormContext();
    const { dropEvent } = useKeySelectionModalContext();
    const { initialProduct = [] } = dropEvent;
    const { field } = useController({ name: 'productId', control: form.control });
    const selectedPlatform = useMemo(() => {
        if (!field.value || !initialProduct.some(isInitialKey)) {
            return undefined;
        }
        return new KeyProductMnemonicIdResolver(field.value).platform;
    }, [field.value]);
    const platformOptions = useMemo(() => initialProduct.filter(isInitialKey).reduce((acc, item) => {
        if (!isInitialKey(item)) {
            return acc;
        }
        const itemPlatform = new KeyProductMnemonicIdResolver(item.id).platform;
        const isDuplicate = acc.some(option => new KeyProductMnemonicIdResolver(option.value).platform === itemPlatform);
        if (!isDuplicate) {
            acc.push({ label: new KeyProductMnemonicIdResolver(item.id).getReadableRegion(), value: item.id });
        }
        return acc;
    }, []), [initialProduct]);
    if (!platformOptions.length) {
        return null;
    }
    return (<ul className="flex flex-wrap justify-center gap-3">
			{platformOptions.map((option) => {
            const keyResolver = new KeyProductMnemonicIdResolver(option.value);
            const isSelected = Boolean(selectedPlatform && selectedPlatform === keyResolver.platform);
            return (<li key={option.value}>
						<button type="button" onClick={() => field.onChange(option.value)} className={tileClass(isSelected)}>
							<span className="flex h-9 w-9 items-center justify-center rounded-full border border-solid border-white/10 bg-secondary/60">
								<Icon color={colorsNew.white} fillColor={colorsNew.white} textColor={colorsNew.white} size={20} icon={platformIconMap[keyResolver.platform]}/>
							</span>
							<span className="text-xs font-bold">{keyResolver.getReadablePlatform()}</span>
						</button>
					</li>);
        })}
		</ul>);
});
const RegionField = memo(() => {
    const form = useKeySelectionFormContext();
    const { dropEvent } = useKeySelectionModalContext();
    const { initialProduct = [] } = dropEvent;
    const { field } = useController({ name: 'productId', control: form.control });
    const selectedPlatform = useMemo(() => {
        if (!field.value || !initialProduct.some(isInitialKey)) {
            return undefined;
        }
        return new KeyProductMnemonicIdResolver(field.value).platform;
    }, [field.value]);
    const regionOptions = useMemo(() => initialProduct.flatMap((item) => {
        if (isInitialKey(item)) {
            const keyResolver = new KeyProductMnemonicIdResolver(item.id);
            if (keyResolver.platform !== selectedPlatform) {
                return [];
            }
            return { label: keyResolver.region, value: item.id };
        }
        if (isInitialCode(item)) {
            return { label: 'Worldwide', value: item.id };
        }
        return [];
    }), [initialProduct, selectedPlatform]);
    if (!regionOptions.length) {
        return null;
    }
    return (<ul className="flex flex-wrap justify-center gap-3">
			{regionOptions.map((option) => {
            const isSelected = field.value === option.value;
            return (<li key={option.value}>
						<button type="button" onClick={() => field.onChange(option.value)} className={tileClass(isSelected)}>
							<span className="text-xs font-bold uppercase">{option.label}</span>
						</button>
					</li>);
        })}
		</ul>);
});
const BundleField = memo(() => {
    const form = useKeySelectionFormContext();
    const { dropEvent } = useKeySelectionModalContext();
    const { initialProduct = [] } = dropEvent;
    const selectedProductId = form.watch('productId');
    const { field } = useController({ name: 'productId', control: form.control });
    const bundleOptions = useMemo(() => initialProduct.filter(isInitialBundle).map(bundle => ({ label: bundle.name, value: bundle.id })), [initialProduct]);
    if (!bundleOptions.length) {
        return null;
    }
    return (<ul className="flex flex-wrap justify-center gap-3">
			{bundleOptions.map((option) => {
            const isSelected = selectedProductId === option.value;
            return (<li key={option.value}>
						<button type="button" onClick={() => field.onChange(option.value)} className={tileClass(isSelected)}>
							<span className="text-xs font-bold uppercase">{option.label}</span>
						</button>
					</li>);
        })}
		</ul>);
});
const DeliveryRateInfo = memo(() => {
    const deliveryRatio = useDeliveryRatio();
    const accountData = useAccountData();
    const pct = Math.min(100, Math.max(0, deliveryRatio * 100));
    const barColor = getDeliveryRatioColor(deliveryRatio);
    return (<div className="flex w-full flex-col items-center gap-2">
			<p className="text-sm font-bold uppercase text-foreground">Your delivery rate</p>
			<div className="h-2.5 w-full max-w-xs overflow-hidden rounded-full bg-secondary">
				<div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: barColor, minWidth: deliveryRatio > 0 ? 8 : 0 }}/>
			</div>
			<span className="text-xs font-semibold tabular-nums text-foreground">{pct.toFixed(2)}%</span>
			<p className="text-center text-xs font-medium text-neon-green">Delivering without a key increases your delivery rate.</p>
			{(accountData === null || accountData === void 0 ? void 0 : accountData.preferredPlatform) === Platform.Twitch && (<p className="text-center text-xs text-muted-foreground">To make a delivery you should stream at least {TWITCH_MIN_STREAM_TIME_MINUTES} minutes.</p>)}
		</div>);
});
const AlreadyOwnConfirm = ({ onConfirm, onCancel }) => {
    const [dismissed, setDismissed] = useDismissAlreadyOwnProductModal();
    return (<RedesignModalShell className="sm:max-w-sm">
			<div className="flex w-full flex-col gap-3 text-sm text-muted-foreground">
				<h3 className="font-display text-base font-semibold text-foreground">Are you sure?</h3>
				<p>Delivering without getting a product <span className="font-semibold text-foreground">improves your delivery rate</span>. You can still deliver dares and earn rewards, but you will not receive an initial key for this drop.</p>
				<p className="font-semibold text-foreground">This cannot be changed after the dare is created.</p>
				<label htmlFor="ks-dismiss" className="flex cursor-pointer select-none items-center gap-2 text-xs">
					<Checkbox id="ks-dismiss" checked={dismissed} onCheckedChange={setDismissed}/>
					Do not show this message again
				</label>
				<div className="flex gap-2">
					<Button variant="secondary" onClick={onCancel} className="flex-1 rounded-lg text-sm">Go back</Button>
					<Button variant="brand" onClick={onConfirm} className="flex-1 rounded-lg text-sm">I understand</Button>
				</div>
			</div>
		</RedesignModalShell>);
};
const Footer = ({ onSubmit, loading, success, error }) => {
    const form = useKeySelectionFormContext();
    const { dropEvent } = useKeySelectionModalContext();
    const { initialProduct = [] } = dropEvent;
    const [dismissAlreadyOwn] = useDismissAlreadyOwnProductModal();
    const redesignModal = useRedesignModal();
    const isDisabled = loading || !form.formState.isValid;
    const label = useMemo(() => {
        if (initialProduct.some(isInitialKey))
            return 'Create dare and get key';
        if (initialProduct.some(isInitialCode))
            return 'Create dare and get code';
        if (initialProduct.some(isInitialBundle))
            return 'Create dare and get bundle';
        return 'Create dare and get product';
    }, [initialProduct]);
    const onAlreadyOwn = () => {
        form.setValue('userHasGame', true);
        if (dismissAlreadyOwn) {
            onSubmit();
            return;
        }
        // Nested confirm on top of this modal (so the base modal does not resize).
        redesignModal.show(<AlreadyOwnConfirm onConfirm={() => { redesignModal.hide(); onSubmit(); }} onCancel={() => { redesignModal.hide(); form.setValue('userHasGame', false); }}/>, { replace: false, closeable: false });
    };
    return (<div className="flex w-full flex-col items-center gap-3">
			<Button type="submit" variant="brand" loading={loading} success={success} error={error} disabled={isDisabled} className="w-full max-w-xs rounded-lg text-sm font-semibold">
				{label}
			</Button>
			<Button type="button" variant="outline" onClick={onAlreadyOwn} className="w-full max-w-xs rounded-lg text-sm">
				I already own this game
			</Button>
		</div>);
};
// Bundle / Platform / Region tiles, shared between the key-selection and reclaim modals.
export const RedesignInitialProductSelect = memo(() => (<div className="flex flex-col items-center gap-4">
		<BundleField />
		<PlatformField />
		<RegionField />
	</div>));
export const RedesignKeySelectionModal = memo((props) => {
    const { dropEvent } = props;
    const { form, onFormSubmit, loading, success, error } = useRedesignKeySelectionModal(props);
    const contextValue = useMemo(() => ({ dropEvent }), [dropEvent]);
    return (<RedesignModalShell className="sm:min-w-[460px] sm:max-w-xl">
			<div className="flex flex-col items-center gap-1 text-center">
				<h2 className="font-display text-xl font-bold text-foreground">Select your reward</h2>
				<p className="text-sm text-muted-foreground">This Drop is available on the options below. Pick one to continue.</p>
			</div>

			<KeySelectionModalContext.Provider value={contextValue}>
				<FormProvider {...form}>
					<form onSubmit={onFormSubmit} className="flex w-full flex-col items-center gap-6">
						<RedesignInitialProductSelect />
						<DeliveryRateInfo />
						<Footer onSubmit={onFormSubmit} loading={loading} success={success} error={error}/>
					</form>
				</FormProvider>
			</KeySelectionModalContext.Provider>
		</RedesignModalShell>);
});
